/**
 * Caméra de poursuite. Port du spring arm Unreal (SpaceShipPawn.cpp:178-193, 1385-1425) :
 * retard de position (vitesse 14, borné à 5,83 m), retard de rotation (16), décalage vertical 5,42 m,
 * champ de vision horizontal 92 → 108° et distance 27,08 → 31,25 m selon le vol.
 * Purement présentationnel : aucune règle de jeu ne dépend de la caméra.
 */
import { Camera, Quaternion, TargetCamera, Vector3, type Scene } from '@babylonjs/core';
import type { CameraData } from '../data';
import { Quat, Vec3, clamp, easeIn, interpTo, lerp } from '../sim/core/math';
import type { FlightState, MotorProfile } from '../sim/entities';

export interface RigTarget {
  position: Vec3;
  rotation: Quat;
  velocity: Vec3;
  flight: FlightState;
  motor: MotorProfile;
  angularVelocity: Vec3;
}

export class CameraRig {
  readonly camera: TargetCamera;
  private laggedPosition: Vec3 | null = null;
  private laggedRotation: Quat | null = null;
  private fov: number;
  private distance: number;
  private shakes: { amplitude: number; duration: number; age: number }[] = [];
  private time = 0;
  private turnOffset = new Vec3();
  reducedMotion = false;

  constructor(scene: Scene, private data: CameraData) {
    this.camera = new TargetCamera('chase', Vector3.Zero(), scene);
    this.camera.fovMode = Camera.FOVMODE_HORIZONTAL_FIXED;
    this.camera.minZ = 0.5;
    this.camera.maxZ = 60000;
    this.camera.rotationQuaternion = new Quaternion();
    this.camera.updateUpVectorFromRotation = true;
    this.fov = data.baseFov;
    this.distance = data.baseDistance;
    this.camera.fov = (this.fov * Math.PI) / 180;
  }

  setData(data: CameraData): void {
    this.data = data;
  }

  /** Secousse de caméra (amplitude en mètres), atténuée en mode « mouvements réduits ». */
  shake(amplitude: number, duration: number): void {
    if (amplitude <= 0) return;
    this.shakes.push({ amplitude: amplitude * (this.reducedMotion ? 0.2 : 1), duration, age: 0 });
    if (this.shakes.length > 8) this.shakes.shift();
  }

  reset(): void {
    this.shakes = [];
    this.laggedPosition = null;
    this.laggedRotation = null;
    this.fov = this.data.baseFov;
    this.distance = this.data.baseDistance;
  }

  update(target: RigTarget, dt: number): void {
    const d = this.data;
    const f = target.flight;
    const m = target.motor;
    const forwardSpeed = Math.max(0, f.signedForwardSpeed);
    const reverseSpeed = Math.max(0, -f.signedForwardSpeed);
    const speed = target.velocity.length();
    const cruiseAlpha = clamp(forwardSpeed / Math.max(m.cruiseMaxSpeed, 1), 0, 1);
    const lateral = Math.sqrt(Math.max(0, speed * speed - f.signedForwardSpeed * f.signedForwardSpeed));
    const lateralAlpha = clamp(lateral / Math.max(m.cruiseMaxSpeed, 1), 0, 1);
    const reverseAlpha = clamp(reverseSpeed / Math.max(m.reverseMaxSpeed, 1), 0, 1);
    const boostAlpha = clamp((forwardSpeed - m.cruiseMaxSpeed) / Math.max(m.boostMaxSpeed - m.cruiseMaxSpeed, 1), 0, 1);
    const feedback = clamp(
      Math.max(d.feedback.cruiseWeight * easeIn(0, 1, cruiseAlpha, d.feedback.cruiseEaseExponent) + d.feedback.boostWeight * boostAlpha, lateralAlpha * d.feedback.lateralWeight),
      0,
      1,
    );
    const motion = this.reducedMotion ? d.reducedMotionScale : 1;
    let targetFov = lerp(d.baseFov, d.maxFov, feedback * motion);
    let targetDistance = lerp(d.baseDistance, d.maxDistance, feedback * motion);
    if (f.reversing) {
      targetFov = d.baseFov + d.reverseFovBonus * reverseAlpha * motion;
      targetDistance = d.baseDistance + d.reverseDistanceBonus * reverseAlpha * motion;
    }
    if (f.braking) {
      targetFov = d.baseFov + d.brakeFovDelta * motion;
      targetDistance = d.baseDistance + d.brakeDistanceDelta * motion;
    }
    if (f.boosting && f.boostElapsed >= 0 && f.boostElapsed < d.boostPulseDuration) {
      const pulse = 1 - f.boostElapsed / Math.max(d.boostPulseDuration, 1e-3);
      targetFov += d.boostPulseFov * pulse * motion;
      targetDistance += d.boostPulseDistance * pulse * motion;
    }
    this.fov = interpTo(this.fov, targetFov, dt, d.fovInterpSpeed);
    this.distance = interpTo(this.distance, targetDistance, dt, d.distanceInterpSpeed);

    // Retard de position (VInterpTo) borné, comme USpringArmComponent::CameraLagMaxDistance.
    if (!this.laggedPosition) this.laggedPosition = target.position.clone();
    const toTarget = target.position.sub(this.laggedPosition);
    this.laggedPosition = this.laggedPosition.add(toTarget.scale(clamp(dt * d.positionLagSpeed, 0, 1)));
    const lag = target.position.sub(this.laggedPosition);
    if (lag.length() > d.positionLagMax) this.laggedPosition = target.position.sub(lag.normalized().scale(d.positionLagMax));

    if (!this.laggedRotation) this.laggedRotation = target.rotation.clone();
    this.laggedRotation = this.laggedRotation.slerp(target.rotation, clamp(dt * d.rotationLagSpeed, 0, 1)).normalized();

    // Bras : derrière le vaisseau dans le repère retardé, décalé vers le haut ; la caméra regarde parallèlement à l'axe.
    const rot = this.laggedRotation;
    // Secousses : décalage pseudo-aléatoire continu, décroissance quadratique.
    this.time += dt;
    let amp = 0;
    this.shakes = this.shakes.filter((s) => (s.age += dt) < s.duration);
    for (const s of this.shakes) amp += s.amplitude * (1 - s.age / s.duration) ** 2;
    const freq = d.shake.frequency, t = this.time;
    const jitter = new Vec3(Math.sin(t * freq * 1.3) + Math.sin(t * freq * 2.7) * 0.5, Math.sin(t * freq * 1.7 + 1.3) + Math.sin(t * freq * 3.1) * 0.5, 0).scale(amp * 0.66);
    // Décalage opposé à la rotation : le vaisseau « vire » dans le cadre au lieu d'y rester figé.
    const w = target.angularVelocity;
    const motionScale = this.reducedMotion ? d.reducedMotionScale : 1;
    const offX = Math.max(-d.turnOffsetMax, Math.min(d.turnOffsetMax, w.y * d.turnOffset)) * motionScale;
    const offY = Math.max(-d.turnOffsetMax, Math.min(d.turnOffsetMax, -w.x * d.turnOffset)) * motionScale;
    this.turnOffset = this.turnOffset.lerp(new Vec3(offX, offY, 0), clamp(dt * 4, 0, 1));
    const position = this.laggedPosition.add(rot.rotate(new Vec3(jitter.x + this.turnOffset.x, d.height + jitter.y + this.turnOffset.y, this.distance)));
    this.camera.position.set(position.x, position.y, position.z);
    this.camera.rotationQuaternion!.set(rot.x, rot.y, rot.z, rot.w);
    this.camera.fov = (this.fov * Math.PI) / 180;
  }
}
